// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./CategoryConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import NewCategoryDialog from "./NewCategoryDialog/NewCategoryDialog";
import EditCategoryDialog from "./EditCategoryDialog/EditCategoryDialog";
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { defaultSort } from "../../Misc/Table/TanStackTableConstants";
import { toTitleCase } from "../../../constants/constants";

const CategoryConsole = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	// raw data
	const [categories, setCategories] = useState([]);
	// table
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// dialogs
	const [newOpen, setNewOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState({});

	// loading
	const [loading, setLoading] = useState(false);

	// access log
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Category Console",
			employeeID: employeeID,
			notes: "Accessed the Category Console page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// fetch on mount
	useEffect(() => {
		fetchCategories();
	}, []);

	// rebuild table when categories change
	useEffect(() => {
		buildColumns();
		buildRows();
	}, [categories]);

	// API load
	const fetchCategories = async () => {
		setLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/tipsAndTricks/categories/all"
			);
			setCategories(res.data);
		} catch (err) {
			console.error("Error fetching categories:", err);
		}
		setLoading(false);
	};

	// new / edit handlers
	const onNewClick = () => setNewOpen(true);
	const onNewClose = () => setNewOpen(false);
	const onEditClick = (rec) => {
		setRecordToEdit(rec);
		setEditOpen(true);
	};
	const onEditClose = () => setEditOpen(false);

	// table builders
	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "category_name",
				header: () => <span>Name</span>,
				label: "Name",
				cell: (props) => <span>{props.getValue()}</span>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: ({ row }) => (
					<IconButton
						onClick={() => onEditClick(row.original.record)}
						size="small"
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const buildRows = () => {
		setRows(
			categories.map((cat) => ({
				category_name: toTitleCase(cat.name),
				edit: "",
				record: cat,
			}))
		);
	};

	return (
		<div className="CategoryConsole">
			<div className="categoryConsoleHeader">
				<div className="categoryConsoleHeaderTitle">
					Category Console
					<Button
						variant="contained"
						color="primary"
						onClick={onNewClick}
						disabled={loading}
						sx={{ ml: 2 }}
					>
						New Category
					</Button>
				</div>
			</div>

			{loading ? (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			) : (
				<div className="tableContainerDiv">
					{categories.length === 0 ? (
						<J2NoResultsFound />
					) : (
						<TanStackTable columnData={columns} rowData={rows} />
					)}
				</div>
			)}

			<NewCategoryDialog
				open={newOpen}
				newCategoryDialogClosed={onNewClose}
				fetchCategories={fetchCategories}
			/>
			<EditCategoryDialog
				open={editOpen}
				recordToEdit={recordToEdit}
				editCategoryDialogClosed={onEditClose}
				fetchCategories={fetchCategories}
			/>
		</div>
	);
};

export default CategoryConsole;
